import { captureException, setContext } from "@sentry/nextjs";
import { parse, format } from "date-fns";
import { fromPromise } from "xstate";

import type {
  ScheduleAppointmentResponse,
  ScheduleAppointmentRequestV2,
} from "@/lib/__generated/api";
import { Application } from "@/lib/__generated/api";
import { scheduleAppointment } from "@/lib/services/scheduleAppointment";
import {
  readScheduleErrorCode,
  readScheduleErrorMessage,
} from "@/lib/utils/schedule-error-response";

interface ScheduleAppointmentInput extends ScheduleAppointmentRequestV2 {
  isMinor: boolean;
  facilityCode?: string;
}

export class ScheduleAppointmentError extends Error {
  constructor(
    message: string,
    public readonly errorCode: string,
  ) {
    super(message);
    this.name = "ScheduleAppointmentError";
  }
}

export function getScheduleAppointmentErrorCode(error: unknown): string {
  return error instanceof ScheduleAppointmentError ? error.errorCode : "UNKNOWN";
}

export const scheduleAppointmentActor = fromPromise<
  ScheduleAppointmentResponse,
  ScheduleAppointmentInput
>(async ({ input }): Promise<ScheduleAppointmentResponse> => {
  try {
    const parsed = (dateOfBirth: string | undefined | null) =>
      parse(dateOfBirth ?? "", "MM/dd/yyyy", new Date());
    const isoDateOfBirth = (dob: string | undefined | null) => format(parsed(dob), "yyyy-MM-dd");
    const rawPhoneNumber = (phoneNumber: string | undefined | null) =>
      phoneNumber?.replaceAll(/\D/g, "") ?? "";
    const isGuarantor = input?.isMinor;

    const result = await scheduleAppointment({
      patient: {
        firstName: input?.patient?.firstName,
        lastName: input?.patient?.lastName,
        email: input?.patient?.email,
        dateOfBirth: isoDateOfBirth(input?.patient?.dateOfBirth),
        phoneNumber: rawPhoneNumber(input?.patient?.phoneNumber),
        phoneType: input?.patient?.phoneType,
        digiact: input?.patient?.digiact,
      },
      ...(isGuarantor && {
        guardian: {
          firstName: input?.guardian?.firstName,
          lastName: input?.guardian?.lastName,
          email: input?.guardian?.email,
          dateOfBirth: isoDateOfBirth(input?.guardian?.dateOfBirth),
          phoneNumber: rawPhoneNumber(input?.guardian?.phoneNumber),
          phoneType: input?.guardian?.phoneType,
        },
      }),
      questionnaireAnsweredQuestions: input?.questionnaireAnsweredQuestions,
      ...(input?.questionnaireId && { questionnaireId: input.questionnaireId }),
      insuranceFlag: input?.insuranceFlag,
      application: Application.AspenDental,
      slotId: input?.slotId,
    });

    const errorCode = readScheduleErrorCode(result);

    if (errorCode) {
      throw new ScheduleAppointmentError(
        readScheduleErrorMessage(result) ?? "Unknown error",
        errorCode,
      );
    }

    if (!result.appointmentNumber) {
      throw new ScheduleAppointmentError(
        "No appointment number returned.",
        "NO_APPOINTMENT_NUMBER",
      );
    }

    return result;
  } catch (error) {
    if (error instanceof ScheduleAppointmentError) {
      setContext("scheduleAppointment", { errorCode: error.errorCode });
      captureException(error, {
        tags: {
          facility_code: input.facilityCode ?? "unknown",
          schedule_error_code: error.errorCode,
        },
        extra: { slot_id: input.slotId, is_minor: input.isMinor },
      });
      throw error;
    }
    captureException(error, {
      tags: {
        facility_code: input.facilityCode ?? "unknown",
        schedule_error_code: "UNKNOWN",
      },
      extra: { slot_id: input.slotId, is_minor: input.isMinor },
    });
    throw new ScheduleAppointmentError(
      error instanceof Error ? error.message : "An unknown error occurred.",
      "UNKNOWN",
    );
  }
});
